import { fmtDate } from "utils/date";
import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Button, Table, Badge, Spinner } from "reactstrap";
import { AlumnoService } from "service/AlumnoService";
import FormMatricula from "./FormMatricula";
import PanelPagos from "./PanelPagos";
import PanelIncidencias from "./PanelIncidencias";

const alumnoService = new AlumnoService();
const API_BASE = process.env.REACT_APP_ROUTE_API || "";

// Helpers para resolver nombre de ciclo/salón
// El endpoint getMatriculasByAlumno devuelve ciclo/salon como IDs simples,
// no como objetos poblados. Usamos los listados maestros como fallback.
const resolveCiclo = (m, listCiclos) => {
  if (m.cicloInfo?.denominacion) return m.cicloInfo.denominacion;
  const id = m.ciclo?._id || m.ciclo;
  return listCiclos.find(c => c._id === id)?.denominacion || id || "-";
};

const resolveSalon = (m, listSalones) => {
  if (m.salonInfo?.denominacion) return m.salonInfo.denominacion;
  const id = m.salon?._id || m.salon;
  return listSalones.find(s => s._id === id)?.denominacion || "-";
};

const PANEL_TITLES = {
  form: (m, lc) => m ? `Editar — ${resolveCiclo(m, lc)}` : "Nueva Matrícula",
  pagos: (m, lc) => `Pagos — ${resolveCiclo(m, lc)}`,
  incidencias: (m, lc) => `Incidencias — ${resolveCiclo(m, lc)}`,
};

const PANEL_COLORS = {
  form: "#1d8cf8",
  pagos: "#00f2c3",
  incidencias: "#ff8d72",
};

const TabMatriculas = ({ alumnoId, alumno, listCiclos, listSalones, listModulos, notify }) => {
  const [listMatriculas, setListMatriculas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePanel, setActivePanel] = useState(null); // null | 'form' | 'pagos' | 'incidencias'
  const [selectedMatricula, setSelectedMatricula] = useState(null);
  const [incidenciasData, setIncidenciasData] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (alumnoId) loadMatriculas();
  }, [alumnoId]);

  const loadMatriculas = async () => {
    setLoading(true);
    try {
      const res = await alumnoService.getMatriculasByAlumno(alumnoId);
      setListMatriculas(res?.data || []);
    } catch (error) {
      notify("danger", "Error al cargar matrículas");
    } finally {
      setLoading(false);
    }
  };

  const closePanel = () => {
    setActivePanel(null);
    setSelectedMatricula(null);
    setIncidenciasData(null);
  };

  const openForm = (m = null) => {
    setSelectedMatricula(m);
    setIncidenciasData(null);
    setActivePanel("form");
  };

  const openPagos = (m) => {
    setSelectedMatricula(m);
    setIncidenciasData(null);
    setActivePanel("pagos");
  };

  const openIncidencias = (m) => {
    setSelectedMatricula(m);
    setIncidenciasData({
      alumno_id: alumnoId,
      matricula_id: m._id,
      nombres: `${alumno.nombres} ${alumno.apellidopaterno}`,
      ciclo: m.cicloInfo || m.ciclo,
      documentoidentidad: alumno.documentoidentidad
    });
    setActivePanel("incidencias");
  };

  const handleVerResumen = (m) => {
    const token = "";
    const cicloId = m.ciclo?._id || m.ciclo;
    const baseSinApi = API_BASE.replace("/api", "");
    const url = `${baseSinApi}/page/asistencia?documentoidentidad=${alumno.documentoidentidad}&matricula=${m._id}&ciclo=${cicloId}&token=${token}`;
    window.open(url, "_blank");
  };

  const handleDeleteMatricula = async (m) => {
    if (!window.confirm(`¿Eliminar la matrícula del ciclo "${resolveCiclo(m, listCiclos)}"?`)) return;
    try {
      await alumnoService.deleteMatricula(m._id);
      notify("success", "Matrícula eliminada correctamente");
      loadMatriculas();
      if (selectedMatricula?._id === m._id) closePanel();
    } catch (error) {
      notify("danger", "Error al eliminar la matrícula");
    }
  };

  const handleMatriculaSaved = () => {
    loadMatriculas();
    closePanel();
    notify("success", "Matrícula guardada correctamente");
  };


  const panelTitle = activePanel ? PANEL_TITLES[activePanel](selectedMatricula, listCiclos) : "";
  const panelColor = activePanel ? PANEL_COLORS[activePanel] : "#344675";

  // Altura máxima de la tabla cuando el panel está activo:
  // suficiente para ver ~8 filas cómodamente y hacer scroll interno
  const TABLE_MAX_HEIGHT = activePanel ? "420px" : "none";

  return (
    <div>
      {/* Cabecera */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="title mb-0">Historial de Matrículas</h6>
        <Button color="success" size="sm" onClick={() => openForm(null)}>
          <i className="tim-icons icon-simple-add mr-1" /> Nueva Matrícula
        </Button>
      </div>

      {/* Layout: tabla | panel (side-by-side cuando panel está activo) */}
      <Row className="no-gutters" style={{ alignItems: "flex-start" }}>

        {/* Columna tabla */}
        <Col md={activePanel ? 6 : 12} style={{ transition: "all 0.2s" }}>
          <div
            style={{
              maxHeight: TABLE_MAX_HEIGHT,
              overflowY: activePanel ? "auto" : "visible",
              overflowX: "auto",
              borderRadius: "4px",
            }}
          >
            <Table size="sm" style={{ marginBottom: 0 }}>
              <thead className="text-primary" style={{ position: "sticky", top: 0, zIndex: 1, background: "#27293d" }}>
                <tr>
                  <th>Ciclo</th>
                  {!activePanel && <th>Salón</th>}
                  <th>Tipo</th>
                  {!activePanel && <th>Fechas</th>}
                  <th>Monto</th>
                  <th>Estado</th>
                  <th className="text-right">Acc.</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activePanel ? 5 : 7} className="text-center py-3">
                      <Spinner size="sm" color="info" />
                    </td>
                  </tr>
                ) : listMatriculas.length === 0 ? (
                  <tr>
                    <td colSpan={activePanel ? 5 : 7} className="text-center text-muted">
                      Sin matrículas registradas
                    </td>
                  </tr>
                ) : listMatriculas.map((m, idx) => {
                  const isSelected = activePanel && selectedMatricula?._id === m._id;
                  return (
                    <tr
                      key={idx}
                      style={isSelected ? { backgroundColor: `${panelColor}18`, borderLeft: `2px solid ${panelColor}` } : {}}
                    >
                      <td>
                        <strong>{resolveCiclo(m, listCiclos)}</strong>
                      </td>
                      {!activePanel && <td>{resolveSalon(m, listSalones)}</td>}
                      <td>
                        <Badge color={m.tipomatricula === "P" ? "primary" : "info"}>
                          {m.tipomatricula === "P" ? "Presencial" : "Virtual"}
                        </Badge>
                      </td>
                      {!activePanel && (
                        <td style={{ whiteSpace: "nowrap" }}>
                          {fmtDate(m.fechadesde)} — {fmtDate(m.fechahasta)}
                        </td>
                      )}
                      <td>S/ {m.montototal || m.costomatriculado || "0"}</td>
                      <td>
                        {m.estado === "1"
                          ? <span className="text-success">Activo</span>
                          : <span className="text-danger">Inactivo</span>}
                      </td>
                      <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                        <Button
                          color="info" size="sm" className="btn-icon btn-link"
                          title="Editar Matrícula"
                          onClick={() => openForm(m)}
                        >
                          <i className="tim-icons icon-pencil" />
                        </Button>
                        <Button
                          color="primary" size="sm" className="btn-icon btn-link"
                          title="Ver Resumen de Asistencia"
                          onClick={() => handleVerResumen(m)}
                        >
                          <i className="tim-icons icon-chart-bar-32" />
                        </Button>
                        <Button
                          color="success" size="sm" className="btn-icon btn-link"
                          title="Pagos"
                          onClick={() => openPagos(m)}
                        >
                          <i className="tim-icons icon-money-coins" />
                        </Button>
                        <Button
                          color="warning" size="sm" className="btn-icon btn-link"
                          title="Incidencias"
                          onClick={() => openIncidencias(m)}
                        >
                          <i className="tim-icons icon-notes" />
                        </Button>
                        <Button
                          color="danger" size="sm" className="btn-icon btn-link"
                          title="Eliminar Matrícula"
                          onClick={() => handleDeleteMatricula(m)}
                        >
                          <i className="tim-icons icon-trash-simple" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Col>

        {/* Columna panel (solo cuando está activo) */}
        {activePanel && (
          <Col md={6} ref={panelRef}>
            <div
              className="ml-3 p-4 rounded"
              style={{
                border: `1px solid ${panelColor}55`,
                borderTop: `3px solid ${panelColor}`,
                background: "rgba(255,255,255,0.02)",
                maxHeight: "520px",
                overflowY: "auto",
              }}
            >
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="title mb-0" style={{ color: panelColor }}>
                  {panelTitle}
                </h6>
                <Button
                  color="link" size="sm" className="text-muted p-0"
                  title="Cerrar panel"
                  onClick={closePanel}
                >
                  <i className="tim-icons icon-simple-remove" />
                </Button>
              </div>
              <hr className="mt-0 mb-3" />

              {activePanel === "form" && (
                <FormMatricula
                  selectedMatricula={selectedMatricula}
                  alumnoId={alumnoId}
                  listCiclos={listCiclos}
                  listSalones={listSalones}
                  listModulos={listModulos}
                  onSave={handleMatriculaSaved}
                  onCancel={closePanel}
                />
              )}

              {activePanel === "pagos" && (
                <PanelPagos
                  matriculaId={selectedMatricula?._id}
                  alumnoNombre={`${alumno.nombres || ""} ${alumno.apellidopaterno || ""}`}
                  onClose={closePanel}
                />
              )}

              {activePanel === "incidencias" && incidenciasData && (
                <PanelIncidencias
                  data={incidenciasData}
                  onClose={closePanel}
                />
              )}
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default TabMatriculas;
